import type { FilePublic } from '@repo/types/files';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { getBase64 } from '@repo/lib/fs/getBase64';
import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { MoreVertical } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { FileIcon } from '@/components/ui/file-icon';
import { FileCard, FileCardContent, FileCardPreview, FileCardPreviewFallback, FileCardPreviewImage, FileCardTitle } from '@/components/ui/fs-card/FileCard';

/**
 * Props for the FsFileCard component.
 * @property {FilePublic} file - The file data to display.
 * @property {boolean} [disabledMenu] - Whether to disable the menu trigger.
 * @property {...ComponentProps<typeof FileCard>} [props] - Additional props for the FileCard.
 */
interface FsFileCardProps extends ComponentProps<typeof FileCard> {
  file: FilePublic;
  disabledMenu?: boolean;
}

export default function FsFileCard(fsFileCardProps: FsFileCardProps) {
  const { file, className, disabledMenu = false, ...props } = fsFileCardProps;
  const thumbnail = useMemo(() => {
    if (!file.thumbnail) return;
    return getBase64({
      fileType: file.fileType,
      base64: file.thumbnail,
      clear: true,
    });
  }, [file.fileType, file.thumbnail]);

  return (
    <FileCard key={file.id} className={cn(className, 'cursor-pointer')} {...props}>
      <FileCardContent>
        <FileIcon fileExtension={getFileExtension(file.name)} />
        <FileCardTitle>{file.name}</FileCardTitle>
        <DropdownMenuTrigger asChild disabled={disabledMenu}>
          <Button variant={'ghost'} size={'sm'}>
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
      </FileCardContent>
      <FileCardPreview>
        <FileCardPreviewImage className={'opacity-85'} src={thumbnail} />
        <FileCardPreviewFallback>
          <FeaturedIcon>
            <FileIcon />
          </FeaturedIcon>
        </FileCardPreviewFallback>
      </FileCardPreview>
    </FileCard>
  );
}
